// The sender's shipping window. One source for the per-package ShippingCountdown
// widget and the /sending + /receiving panel rows, so both surfaces produce the
// same percentage and the same band for the same trade.

export const SHIP_WINDOW_HOURS = 72;
export const SHIP_WINDOW_MS = SHIP_WINDOW_HOURS * 60 * 60 * 1000;

export type ShippingBand = 'normal' | 'warning' | 'critical' | 'overdue';

/**
 * Parse a window-start string to epoch ms. Handles ISO (from the API) and the
 * legacy M/D/YY format. Returns null when the string has too few date parts,
 * and NaN when it has the right shape but does not parse; callers treat the
 * two differently, as ShippingCountdown always has.
 */
export const parseWindowStart = (value: string): number | null => {
  if (value.includes('T') || value.includes('-')) {
    return new Date(value).getTime();
  }

  const dateParts = value.split('/');
  if (dateParts.length < 3) return null;

  const [month, day, year] = dateParts;
  const fullYear = year && year.length === 2 ? `20${year}` : year;
  return new Date(`${month}/${day}/${fullYear}`).getTime();
};

export const getShipDeadline = (startMs: number): number => startMs + SHIP_WINDOW_MS;

/** Percentage of the window REMAINING: 100 at the start, 0 at the deadline. */
export const getProgressPercentage = (remainingMs: number): number =>
  Math.max(0, Math.min(100, (remainingMs / SHIP_WINDOW_MS) * 100));

export const getShippingBand = (percentage: number, isExpired: boolean): ShippingBand => {
  if (isExpired) return 'overdue';
  if (percentage < 25) return 'critical'; // Less than 18 hours remaining
  if (percentage < 50) return 'warning'; // Less than 36 hours remaining
  return 'normal';
};

export interface ShippingWindowState {
  isExpired: boolean;
  percentage: number;
  band: ShippingBand;
}

/**
 * Window state at `nowMs`. Remaining time is truncated to whole seconds before
 * the percentage is taken, because ShippingCountdown derives its percentage
 * from h/m/s state; without the truncation the two would differ in the
 * fractional digits.
 */
export const getShippingWindowState = (startMs: number, nowMs: number): ShippingWindowState => {
  const difference = getShipDeadline(startMs) - nowMs;
  if (difference <= 0) {
    return { isExpired: true, percentage: 0, band: 'overdue' };
  }
  const percentage = getProgressPercentage(Math.floor(difference / 1000) * 1000);
  return { isExpired: false, percentage, band: getShippingBand(percentage, false) };
};
